import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import {
  errorMessage,
  getAuthSession,
  setAuthChallengeHandler,
  setMfaToken,
  setSessionTokenProvider,
} from "@/api/entities";

/**
 * Operator identity for the whole app.
 *
 * `phase` is the single source of truth for what the user is allowed to see:
 *
 *   loading     — still working out who this is; show nothing
 *   signed_out  — no Google session; show the sign-in screen
 *   denied      — signed in, but this Google account is not on the allowlist
 *   enrol       — allowlisted, but no authenticator app paired yet (first login)
 *   verify      — paired, but this session has not entered a code yet
 *   ready       — all three checks passed; the app is usable
 *   error       — the server could not be reached or is misconfigured
 *
 * Only `ready` renders the application. The server enforces exactly the same
 * rules, so this context is for a coherent UI, never the security boundary.
 */

const OperatorContext = createContext(null);

const PHASE_FOR_STEP = {
  ready: "ready",
  enrol_authenticator: "enrol",
  enter_code: "verify",
};

export function OperatorProvider({ children }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useAuth();
  const { user } = useUser();

  const [phase, setPhase] = useState("loading");
  const [email, setEmail] = useState("");
  const [backupCodesRemaining, setBackupCodesRemaining] = useState(null);
  const [error, setError] = useState("");

  // Register the token source before anything can issue a request.
  useEffect(() => {
    setSessionTokenProvider(() => getToken());
  }, [getToken]);

  const refresh = useCallback(async () => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setPhase("signed_out");
      setEmail("");
      setError("");
      return;
    }
    try {
      const session = await getAuthSession();
      setEmail(session.email || "");
      setBackupCodesRemaining(session.backup_codes_remaining ?? null);
      setPhase(PHASE_FOR_STEP[session.next_step] || "verify");
      setError("");
    } catch (err) {
      const status = err?.response?.status;
      const clerkEmail = user?.primaryEmailAddress?.emailAddress || "";
      if (status === 403) {
        // Signed in with Google, but not permitted to be here.
        setPhase("denied");
        setEmail(clerkEmail);
      } else if (status === 401) {
        // The server rejected the session itself (expired or not yet valid).
        // Send them back to sign in rather than stranding them on an error page.
        setPhase("signed_out");
        setEmail("");
      } else {
        setPhase("error");
      }
      setError(errorMessage(err, "Could not verify your access."));
    }
  }, [isLoaded, isSignedIn, user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // If a request comes back saying the second factor lapsed, drop straight back
  // to the code prompt instead of letting the page show stale data.
  useEffect(() => {
    setAuthChallengeHandler((code) => {
      setPhase(code === "mfa_enrolment_required" ? "enrol" : "verify");
    });
    return () => setAuthChallengeHandler(null);
  }, []);

  const logout = useCallback(async () => {
    setMfaToken("");
    setPhase("signed_out");
    try {
      await signOut();
    } catch {
      /* already gone */
    }
  }, [signOut]);

  const value = useMemo(
    () => ({
      phase,
      email,
      error,
      backupCodesRemaining,
      refresh,
      logout,
      // Kept for the existing components that ask these questions.
      ready: phase !== "loading",
      authed: phase === "ready",
      canWrite: phase === "ready",
      authRequired: true,
    }),
    [phase, email, error, backupCodesRemaining, refresh, logout]
  );

  return <OperatorContext.Provider value={value}>{children}</OperatorContext.Provider>;
}

export function useOperator() {
  const ctx = useContext(OperatorContext);
  if (!ctx) throw new Error("useOperator must be used within OperatorProvider");
  return ctx;
}
